/**
 * <SalesMovers rows={hub.rows} compareLabel="vs previous 30 days" onOpen={sku => setOpenSku(sku)} loading={loading} />
 *
 * "What changed": the top 5 gainers and top 5 decliners among SKUs, ranked by ₹ revenue change
 * (revenue_delta from /api/sales-hub?dimension=sku), never by % alone. Each row shows the product,
 * a DeltaBadge (₹ and %, or "New") and units now vs before; clicking opens the product drawer.
 * (Replaces the old fixed 7-day "Needs attention" block.)
 */
import React, { useMemo } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { DeltaBadge } from './DeltaBadge';
import { number, shortMoney } from './format';
import { topMovers } from './overview/trend';

const MoverList = ({ id, title, icon: Icon, tone, items, empty, onOpen, loading }) => (
  <div className="min-w-0" data-testid={`sales-movers-${id}`}>
    <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
      <Icon className={`h-4 w-4 ${tone}`} aria-hidden="true" />{title}
    </h3>
    {loading && !items.length ? (
      <div className="space-y-2">{[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
    ) : !items.length ? (
      <p className="py-3 text-xs text-gray-500">{empty}</p>
    ) : (
      <ol className="divide-y divide-gray-100">
        {items.map(row => (
          <li key={row.key}>
            <button type="button" onClick={() => onOpen?.(row.key)} data-testid={`sales-mover-${row.key}`}
              aria-label={`Open ${row.product_name || row.key}`}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900">{row.product_name || row.key}</span>
                <span className="block truncate text-xs text-gray-500">{row.key}{row.brand ? ` · ${row.brand}` : ''}</span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">
                <DeltaBadge current={row.revenue} previous={row.previous_revenue} size="xs" />
                <span className="text-[11px] text-gray-500">{shortMoney(row.revenue)} · {number(row.units)} units (was {number(row.previous_units)})</span>
              </span>
            </button>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export const SalesMovers = ({ rows = [], compareLabel, onOpen, loading = false }) => {
  const { gainers, decliners } = useMemo(() => topMovers(rows, 5), [rows]);
  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="sales-movers" aria-busy={loading}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">What changed</h2>
        <span className="text-xs text-gray-500">Biggest ₹ change by product {compareLabel}</span>
      </div>
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${loading && rows.length ? 'opacity-60' : ''}`}>
        <MoverList id="gainers" title="Biggest gains" icon={TrendingUp} tone="text-green-600" items={gainers} onOpen={onOpen} loading={loading}
          empty="No product sold more than in the comparison period." />
        <MoverList id="decliners" title="Biggest drops" icon={TrendingDown} tone="text-red-600" items={decliners} onOpen={onOpen} loading={loading}
          empty="No product sold less than in the comparison period." />
      </div>
    </section>
  );
};

/** Kept for older imports: the overview no longer shows a separate exceptions strip. */
export const SalesExceptions = SalesMovers;
export default SalesMovers;
